import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { UserService } from '../../service/user.service';
import { InitialsPipe } from '../../../../shared/pipes/initials.pipe';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { isParentKycRequiredError } from '../../../identity/helpers/identity-error.helper';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { ParentIdentityService } from '../../../identity/services/parent-identity.service';
import { KidDocumentVerifyComponent } from '../../../identity/components/kid-document-verify/kid-document-verify.component';
import {
  FamilyKidsService,
  type FamilyChild,
} from '../../../parent-family/services/family-kids.service';

@Component({
  selector: 'app-pending-kid',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    TranslatePipe,
    InitialsPipe,
    ParentButtonComponent,
    KidDocumentVerifyComponent,
  ],
  templateUrl: './pending-kid.component.html',
  styleUrl: './pending-kid.component.scss',
})
export class PendingKidComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<PendingKidComponent>);
  #userService = inject(UserService);
  #familyKids = inject(FamilyKidsService);
  #identityNav = inject(IdentityNavigationService);
  #identity = inject(ParentIdentityService);
  data = inject<{ user: FamilyChild }>(MAT_DIALOG_DATA);

  readonly requiresDocumentVerification = computed(() =>
    this.#identity.layerEnabled(),
  );

  readonly isVerifying = computed(() => this.#userService.verifyKid.isPending());

  ngOnInit(): void {
    void this.#identity.whenConfigReady();
  }

  async verify(): Promise<void> {
    if (this.isVerifying()) {
      return;
    }

    try {
      await this.#userService.verifyKid.mutateAsync(this.data.user.id);
      await this.#familyKids.refetch();
      this.#dialogRef.close();
    } catch (error) {
      if (isParentKycRequiredError(error)) {
        this.#dialogRef.close();
        await this.#identityNav.navigateToVerification();
      }
    }
  }

  async onVerificationCompleted(): Promise<void> {
    await this.#userService.reloadProfile();
    await this.#familyKids.refetch();
    this.#dialogRef.close();
  }

  discard(): void {
    this.#dialogRef.close();
  }
}
